import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';
import { Pencil, Trash2, Plus, ArrowLeft } from 'lucide-react';
import { useI18n } from '../context/useI18n';
import PhoneField from './ui/PhoneField';

const DEFAULT_FORM = {
  nombre: '',
  descripcion: '',
  precio_mensual: 0,
  precio_mensual_usd: 0,
  max_users: 0,
  features: '',
  program_id: 'virtual_d28d',
  kind: 'd28d',
  module_access: '{}',
  cycle_ids: [],
  is_couple: false,
  included_seats: 1,
  support_whatsapp: '573192635819',
  support_name: 'Soporte D28D',
  support_message: '',
  support_activo: true,
  cycles_count: 1,
  activo: true,
  visible: true,
  sort_order: 0,
  training_audience: 'client',
  for_sedes: false,
};

const TRAINING_AUDIENCES = ['client', 'coach'];

const TRAINING_AUDIENCE_LABELS = {
  client: 'Usuario final (Wompi directo)',
  coach: 'Entrenador (activación clientes)',
};

 function resolveDefaultProgramId({ programId = null, gymId = null, fixedKind = null, programs = [] } = {}) {
   if (programId) return programId;
   if (fixedKind === 'training') return 'training';
   if (fixedKind === 'food') return 'food';
   if (programs.length > 0) return String(programs[0].id);
   return gymId ? '' : 'virtual_d28d';
 }

function resolveGymIdFromProgram(programId, programs = []) {
  if (!programId) return null;
  const program = programs.find((p) => String(p.id) === String(programId));
  if (program?.gym_id != null && Number.isFinite(Number(program.gym_id))) {
    return Number(program.gym_id);
  }
  const m = String(programId).match(/^g(\d+)_/i);
  return m ? Number(m[1]) : null;
}

export default function AdminPlans({
  mode = null,
  onBack = null,
  singlePlanOnly = false,
  title = null,
  embedded = false,
  programId = null,
  hideProgramColumn = false,
  gymId = null,
} = {}) {
  const fixedKind = mode === 'training'
    ? 'training'
    : mode === 'food'
      ? 'food'
      : mode === 'd28d'
        ? 'd28d'
        : null;
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingPlan, setEditingPlan] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const { t } = useI18n();

  const planKey = (nombre) => encodeURIComponent(String(nombre || ''));
  const planLabel = (plan) => plan?.display_nombre || plan?.nombre || '';
  const isPlanActive = (plan) => plan.activo !== false && plan.visible !== false;

  const initialForm = useMemo(() => ({
    ...DEFAULT_FORM,
    kind: fixedKind || 'd28d',
    support_name: gymId ? 'Soporte' : DEFAULT_FORM.support_name,
    program_id: resolveDefaultProgramId({ programId, gymId, fixedKind }),
  }), [fixedKind, programId, gymId]);

  const [formData, setFormData] = useState(initialForm);
  const [programs, setPrograms] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [scopeGymIsSede, setScopeGymIsSede] = useState(false);

  useEffect(() => {
    if (!gymId) {
      setScopeGymIsSede(false);
      return;
    }
    let cancelled = false;
    api.get(`/gyms/${gymId}`)
      .then((res) => {
        if (cancelled) return;
        const g = res.data?.data || res.data || {};
        const isSede = g.parent_gym_id != null && g.parent_gym_id !== '';
        setScopeGymIsSede(Boolean(isSede));
        if (isSede) {
          setFormData((prev) => (prev.for_sedes ? prev : { ...prev, for_sedes: true }));
        }
      })
      .catch(() => {
        if (!cancelled) setScopeGymIsSede(Boolean(gymId));
      });
    return () => { cancelled = true; };
  }, [gymId]);

  const fetchPlans = useCallback(async () => {
    try {
      const params = {};
      if (fixedKind) params.kind = fixedKind;
      if (programId) params.program_id = programId;
      if (gymId) params.gym_id = gymId;
      else if (!fixedKind || fixedKind === 'd28d') {
        params.platform_only = 'true';
        // Incluir planes for_sedes en la tabla admin (el API los oculta por defecto en platform_only).
        params.include_for_sedes = 'true';
      }
      // Maestro: ver activos e inactivos para Desactivar/Activar (altas/registro solo usan activos).
      params.include_inactive = 'true';
      const res = await api.get('/accounts/plans', { params });
      let plansData = Array.isArray(res.data) ? res.data : (res.data.data || []);
      if (fixedKind) {
        plansData = plansData.filter((p) => String(p.kind || '') === fixedKind);
      }
      if (programId) {
        plansData = plansData.filter((p) => String(p.program_id) === String(programId));
      }
      plansData.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      setPlans(plansData);
    } catch (err) {
      setError(t('plans.error_loading', 'Error cargando planes'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [t, fixedKind, programId, gymId]);

  const fetchPrograms = useCallback(async () => {
    if (fixedKind === 'training' || fixedKind === 'food') {
      setPrograms([]);
      return;
    }
    try {
      const params = {};
      if (gymId) {
        params.gym_id = gymId;
        params.gym_only = 'true';
      } else if (!fixedKind || fixedKind === 'd28d') {
        params.platform_only = 'true';
      }
      const res = await api.get('/programs', { params });
      setPrograms(res.data.data || []);
    } catch (err) {
      console.error('Error loading programs', err);
    }
  }, [gymId, fixedKind]);

  const fetchCycles = useCallback(async () => {
    if (fixedKind === 'training' || fixedKind === 'food') {
      setCycles([]);
      return;
    }
    try {
      const params = {};
      if (gymId) {
        params.gym_id = gymId;
        params.gym_only = 'true';
      } else if (!fixedKind || fixedKind === 'd28d') {
        params.platform_only = 'true';
      }
      const res = await api.get('/cycles', { params });
      setCycles(res.data?.data || []);
    } catch (err) {
      console.error('Error loading cycles', err);
    }
  }, [gymId, fixedKind]);

  useEffect(() => {
    fetchPlans();
    fetchPrograms();
    fetchCycles();
  }, [fetchPlans, fetchPrograms, fetchCycles]);

  useEffect(() => {
    if (fixedKind === 'training' || fixedKind === 'food' || programId) return;
    if (!programs.length) return;
    setFormData((prev) => {
      const hasCurrentProgram = programs.some((p) => String(p.id) === String(prev.program_id));
      if (hasCurrentProgram) return prev;
      return {
        ...prev,
        program_id: String(programs[0].id),
      };
    });
  }, [programs, fixedKind, programId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (gymId && !programId && !formData.program_id) {
      setError(t('gym.plans.need_programs', 'Primero crea un programa en Maestros → Programas para poder asociarlo al plan.'));
      return;
    }
    try {
      let parsedModuleAccess = {};
      try { parsedModuleAccess = JSON.parse(formData.module_access || '{}'); } catch { parsedModuleAccess = {}; }
      const payload = {
        ...formData,
        kind: fixedKind || formData.kind,
        gym_id: gymId || resolveGymIdFromProgram(programId || formData.program_id, programs) || undefined,
        program_id: programId || (fixedKind === 'training'
          ? 'training'
          : fixedKind === 'food'
            ? 'food'
            : formData.program_id),
        max_usuarios: Number(formData.max_users) || 0,
        precio_mensual: Number(formData.precio_mensual) || 0,
        precio_mensual_usd: Number(formData.precio_mensual_usd) || 0,
        included_seats: Number(formData.included_seats) || 1,
        is_couple: fixedKind === 'training' ? false : !!formData.is_couple,
        module_access: parsedModuleAccess,
        cycle_ids: (fixedKind === 'training' || fixedKind === 'food')
          ? []
          : (Array.isArray(formData.cycle_ids) ? formData.cycle_ids.map((n) => Number(n)).filter(Boolean) : []),
        features: formData.features.split(',').map(f => f.trim()).filter(Boolean),
        support_whatsapp: formData.support_whatsapp,
        support_name: formData.support_name,
        support_message: formData.support_message,
        support_activo: formData.support_activo !== false,
        cycles_count: fixedKind === 'food'
          ? null
          : (fixedKind === 'training' || formData.kind === 'training')
            ? (Number(formData.cycles_count) > 0 ? Number(formData.cycles_count) : 1)
            : (Number(formData.cycles_count) || null),
        activo: formData.activo !== false,
        visible: formData.activo !== false,
        sort_order: Number(formData.sort_order) || 0,
        training_audience: (fixedKind === 'training' || formData.kind === 'training')
          ? (formData.training_audience || 'client')
          : undefined,
        for_sedes: (fixedKind === 'd28d' || formData.kind === 'd28d')
          ? (scopeGymIsSede || formData.for_sedes === true)
          : false,
      };
      if (editingPlan) {
        const payloadForUpdate = { ...payload };
        const trimmedName = String(formData.nombre || '').trim();
        if (trimmedName === String(planLabel(editingPlan) || '').trim()) {
          delete payloadForUpdate.nombre;
        }
        await api.put(`/accounts/plans/${planKey(editingPlan.nombre)}`, payloadForUpdate);
      } else {
        // Create logic
        await api.post('/accounts/plans', {
          ...payload,
        });
      }
      setEditingPlan(null);
      setFormData({ ...initialForm });
      setShowForm(false);
      setError('');
      fetchPlans();
    } catch (err) {
      const msg = err.response?.data?.error || 'Error al guardar plan';
      setError(msg.includes('encontrado') || msg.includes('duplicad')
        ? 'No se pudo guardar: revisa que el nombre del plan no esté repetido.'
        : msg);
    }
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    setFormData({
      nombre: planLabel(plan),
      descripcion: plan.descripcion,
      precio_mensual: plan.precio_mensual,
      precio_mensual_usd: plan.precio_mensual_usd || 0,
      max_users: plan.max_users || 0,
      features: Array.isArray(plan.features) ? plan.features.join(', ') : plan.features,
      program_id: plan.program_id || 'virtual_d28d',
      kind: plan.kind || 'd28d',
      module_access: JSON.stringify(plan.module_access || {}, null, 2),
      cycle_ids: Array.isArray(plan.cycle_ids) ? plan.cycle_ids : [],
      is_couple: !!plan.is_couple,
      included_seats: plan.included_seats || 1,
      support_whatsapp: plan.support_whatsapp || '573192635819',
      support_name: plan.support_name || 'Soporte D28D',
      support_message: plan.support_message || '',
      support_activo: plan.support_activo !== false,
      cycles_count: plan.cycles_count ?? 1,
      activo: isPlanActive(plan),
      visible: isPlanActive(plan),
      sort_order: plan.sort_order || 0,
      training_audience: plan.training_audience || 'client',
      for_sedes: plan.for_sedes === true,
    });
    setShowAdvanced(false);
    setShowForm(true);
  };

  const openCreateForm = () => {
    setEditingPlan(null);
    const defaultProgram = resolveDefaultProgramId({
      programId,
      gymId,
      fixedKind,
      programs,
    });
    setFormData({
      ...initialForm,
      program_id: defaultProgram,
      training_audience: fixedKind === 'training' ? nextTrainingAudience : initialForm.training_audience,
    });
    setShowAdvanced(false);
    setShowForm(true);
  };

  const handleDelete = async (nombre) => {
    if (!window.confirm(t('plans.delete_confirm', '¿Seguro que deseas eliminar este plan?'))) return;
    try {
      const res = await api.delete(`/accounts/plans/${planKey(nombre)}`);
      if (res.data?.soft_deleted) {
        setError(res.data.message || t('plans.soft_deleted', 'Plan ocultado del catálogo (tenía cuentas vinculadas).'));
      } else {
        setError('');
      }
      fetchPlans();
    } catch (err) {
      setError(err?.response?.data?.error || t('plans.delete_error', 'Error al eliminar plan'));
    }
  };

  const togglePlanActive = async (plan) => {
    const next = !isPlanActive(plan);
    try {
      await api.put(`/accounts/plans/${planKey(plan.nombre)}`, {
        activo: next,
        visible: next,
      });
      fetchPlans();
    } catch (err) {
      setError(err?.response?.data?.error || 'Error al cambiar estado del plan');
    }
  };

  const hasCoachTrainingPlan = useMemo(() => {
    if (fixedKind !== 'training') return false;
    return plans.some((p) => String(p.training_audience || 'client') === 'coach');
  }, [plans, fixedKind]);

  const nextTrainingAudience = 'client';

  const canAddTrainingPlan = fixedKind !== 'training' || true;

  const showProgramColumn = !hideProgramColumn && fixedKind !== 'training' && fixedKind !== 'food';
  const showCyclesColumn = fixedKind !== 'food';
  const showTrainingMonths = fixedKind === 'training' || formData.kind === 'training';
  const showD28dCycles = showCyclesColumn && !showTrainingMonths;

  const tableColSpan = useMemo(() => {
    let cols = 6;
    if (showProgramColumn) cols += 1;
    if (fixedKind === 'training') cols += 1;
    if (showTrainingMonths || showD28dCycles) cols += 1;
    if (fixedKind === 'd28d' || !fixedKind) cols += 1;
    return cols;
  }, [showProgramColumn, fixedKind, showTrainingMonths, showD28dCycles]);

  const pageTitle = title || (fixedKind === 'training'
    ? 'Plan comercial Entrenadores'
    : fixedKind === 'food'
      ? 'Plan comercial FOOD_PLAN'
    : fixedKind === 'd28d'
      ? 'Planes D28D por programa'
      : t('plans.title', 'Planes de Suscripción'));
  const canAddPlan = fixedKind === 'training'
    ? canAddTrainingPlan
    : (!singlePlanOnly || plans.length < 1);

  return (
    <div className="space-y-6 notranslate" translate="no">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div className="flex items-center gap-3">
          {!embedded && onBack && (
            <button type="button" className="btn-secondary" onClick={onBack}>
              <ArrowLeft className="w-4 h-4 inline mr-1" />
              Volver
            </button>
          )}
          {!embedded && <h3 className="text-lg font-bold text-stone-900" translate="no">{pageTitle}</h3>}
          {embedded && (
            <div className="space-y-1">
              <h3 className="text-lg font-bold text-stone-900" translate="no">
                {gymId ? (title || t('gym.masters.plans', 'Planes comerciales')) : pageTitle}
              </h3>
              <p className="text-sm text-stone-600">
                {gymId
                  ? t('gym.masters.plans_embedded_hint', 'Crea un plan con «Crear plan» y en el formulario marca los ciclos incluidos.')
                  : 'Activo = visible en registro público. Inactivo = no aparece al crear usuario.'}
              </p>
            </div>
          )}
        </div>
        {!showForm && canAddPlan && (
          <button
            type="button"
            className="btn-primary inline-flex items-center gap-2"
            onClick={openCreateForm}
          >
            <Plus className="w-4 h-4" />
            Crear plan
          </button>
        )}
      </div>
      {singlePlanOnly && fixedKind === 'food' && plans.length >= 1 && !showForm && (
        <p className="text-sm text-stone-600">
          Solo puede existir un plan comercial de FOOD_PLAN. Edita el existente.
        </p>
      )}
      {fixedKind === 'training' && !showForm && (
        <p className="text-sm text-stone-600">
          Crea tantos planes <strong>usuario final (Wompi)</strong> como necesites; tus clientes podrán elegir al registrarse.
          El plan <strong>Entrenador (activación clientes)</strong> es único y solo aplica a nuevos entrenadores marca blanca.
        </p>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded-xl text-sm">
          <p className="font-medium">{error}</p>
        </div>
      )}

      {showForm ? (
        <div className="ui-light-panel d28d-admin-surface admin-plans-form rounded-xl border border-slate-200 p-5 notranslate" translate="no">
          <div className="flex justify-between items-center mb-4 border-b border-slate-200 pb-2">
            <h4 className="text-md font-semibold">{editingPlan ? t('plans.edit', 'Editar plan') : t('plans.create', 'Crear plan')}</h4>
            <button 
              className="text-sm text-stone-500 hover:text-stone-900" 
              onClick={() => setShowForm(false)}
            >
              {t('common.cancel', 'Cancelar')}
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            {fixedKind !== 'training' && fixedKind !== 'food' && (
              <div>
                <label className="label">Programa</label>
                {gymId && programs.length === 0 ? (
                  <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
                    {t('gym.plans.need_programs', 'Primero crea un programa en Maestros → Programas para poder asociarlo al plan.')}
                  </p>
                ) : (
                <select
                  name="program_id"
                  value={programId || formData.program_id}
                  onChange={handleInputChange}
                  required
                  className="input"
                  disabled={fixedKind === 'training' || fixedKind === 'food' || !!programId}
                >
                  {!formData.program_id && gymId ? (
                    <option value="">{t('common.select', 'Seleccionar…')}</option>
                  ) : null}
                  {programs.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                      {p.gym_id != null ? ` (Gym #${p.gym_id})` : ' (Plataforma D28D)'}
                    </option>
                  ))}
                </select>
                )}
                {!gymId && resolveGymIdFromProgram(programId || formData.program_id, programs) == null
                  && fixedKind !== 'training' && fixedKind !== 'food' ? (
                  <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2">
                    Este plan queda en catálogo plataforma D28D. No aparecerá al registrar usuarios con código de gimnasio (ej. GYM-D28D-004).
                    Para planes de un gym virtual, usa Maestros → Planes comerciales o elige un programa marcado como Gym #N.
                  </p>
                ) : null}
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {showTrainingMonths && (
              <div>
                <label className="label">Meses de vigencia</label>
                <input
                  name="cycles_count"
                  type="number"
                  min="1"
                  max="24"
                  className="input"
                  value={formData.cycles_count || 1}
                  onChange={handleInputChange}
                />
                <p className="text-xs text-stone-500 mt-1">1 = mensual, 2 = bimestral, 3 = trimestral…</p>
              </div>
              )}
              {showD28dCycles && (
              <div>
                <label className="label">Ciclos incluidos</label>
                <input name="cycles_count" type="number" min="1" className="input"
                  value={formData.cycles_count} onChange={handleInputChange} />
              </div>
              )}
              <div>
                <label className="label">Orden</label>
                <input name="sort_order" type="number" className="input"
                  value={formData.sort_order} onChange={handleInputChange} />
              </div>
              <label className="flex items-center gap-2 text-sm pt-6 md:col-span-2">
                <input type="checkbox" checked={!!formData.activo}
                  onChange={(e) => setFormData((p) => ({
                    ...p,
                    activo: e.target.checked,
                    visible: e.target.checked,
                  }))} />
                Activo (disponible en registro de usuarios nuevos)
              </label>
              {(fixedKind === 'd28d' || formData.kind === 'd28d') && (
                <label className="flex items-center gap-2 text-sm pt-2 md:col-span-4 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={scopeGymIsSede || formData.for_sedes === true}
                    disabled={scopeGymIsSede}
                    onChange={(e) => setFormData((p) => ({ ...p, for_sedes: e.target.checked }))}
                  />
                  {t('plans.for_sedes', 'Sedes')}
                  <span className="text-xs text-stone-500">
                    {scopeGymIsSede
                      ? t('plans.for_sedes_sede_locked', 'Obligatorio en sede: visible en registro y alta de usuarios de esta sede.')
                      : t('plans.for_sedes_hint', 'Solo visible para usuarios de sedes (registro y alta por admin sede).')}
                  </span>
                </label>
              )}
            </div>
            {!fixedKind && (
              <div>
                <label className="label">Tipo</label>
                <select name="kind" value={formData.kind} onChange={handleInputChange} className="input">
                <option value="d28d">D28D</option>
                <option value="food">FOOD_PLAN</option>
                <option value="training">Entrenadores</option>
              </select>
              </div>
            )}
            {fixedKind === 'training' && (
              <div>
                <label className="label">Aplica a</label>
                <select
                  name="training_audience"
                  value={formData.training_audience || 'client'}
                  onChange={handleInputChange}
                  className="input"
                  disabled={Boolean(editingPlan)}
                >
                  {TRAINING_AUDIENCES.map((audience) => {
                    const taken = audience === 'coach'
                      && hasCoachTrainingPlan
                      && !editingPlan
                      && formData.training_audience !== 'coach';
                    return (
                      <option key={audience} value={audience} disabled={taken}>
                        {TRAINING_AUDIENCE_LABELS[audience]}
                        {taken ? ' (ya existe)' : ''}
                      </option>
                    );
                  })}
                </select>
              </div>
            )}
            {fixedKind === 'training' && (
              <input type="hidden" name="kind" value="training" />
            )}
            <div>
              <label className="label" translate="no">{t('plans.name', 'Nombre del plan')}</label>
              <input
                name="nombre"
                value={formData.nombre}
                onChange={handleInputChange}
                required
                className="input"
                placeholder="Ej: Vital D28D - Mensual"
              />
              {editingPlan && (
                <p className="text-xs text-stone-500 mt-1">
                  Si cambias el nombre, las cuentas vinculadas se actualizan automáticamente.
                </p>
              )}
            </div>
            <div>
              <label className="label">{t('common.description', 'Descripción')}</label>
              <textarea 
                name="descripcion" 
                value={formData.descripcion} 
                onChange={handleInputChange} 
                required 
                rows="3"
                className="input"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">Precio COP</label>
                <input 
                  name="precio_mensual" 
                  type="number" 
                  value={formData.precio_mensual} 
                  onChange={handleInputChange} 
                  required 
                  className="input"
                />
              </div>
              <div>
                <label className="label">Precio USD</label>
                <input
                  name="precio_mensual_usd"
                  type="number"
                  value={formData.precio_mensual_usd}
                  onChange={handleInputChange}
                  required
                  className="input"
                />
              </div>
              <div>
                <label className="label">{t('plans.max_users', 'Máximo de Usuarios')}</label>
                <input 
                  name="max_users" 
                  type="number" 
                  min="0"
                  value={formData.max_users} 
                  onChange={handleInputChange} 
                  className="input"
                />
              </div>
            </div>
            {(fixedKind !== 'training' && formData.kind !== 'training') && (
            <div>
              <label className="label">{gymId ? 'Ciclos permitidos' : 'Ciclos permitidos (D28D)'}</label>
              <p className="text-xs text-slate-600 mb-2">
                Marca los ciclos que el plan puede vender en registro. Ver la lista no basta: deben quedar
                {' '}
                <strong>marcados y guardados</strong>
                {' '}
                ({(formData.cycle_ids || []).length} seleccionados).
              </p>
              <div
                className="input max-h-40 overflow-y-auto space-y-1 py-2"
                style={{ minHeight: 120 }}
              >
                {cycles.length === 0 ? (
                  <p className="text-sm text-slate-500">No hay ciclos en este alcance.</p>
                ) : cycles.map((c) => {
                  const id = Number(c.id);
                  const checked = (formData.cycle_ids || []).map(Number).includes(id);
                  return (
                    <label key={c.id} className="flex items-center gap-2 text-sm text-slate-800 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          setFormData((prev) => {
                            const cur = (prev.cycle_ids || []).map(Number).filter(Number.isFinite);
                            const next = e.target.checked
                              ? Array.from(new Set([...cur, id]))
                              : cur.filter((x) => x !== id);
                            return { ...prev, cycle_ids: next };
                          });
                        }}
                      />
                      <span>{c.name} ({c.startDate})</span>
                    </label>
                  );
                })}
              </div>
            </div>
            )}
            {!gymId && (
            <div className="border-t border-stone-200 pt-3">
              <button
                type="button"
                className="text-sm font-medium text-indigo-700 hover:underline"
                onClick={() => setShowAdvanced((v) => !v)}
              >
                {showAdvanced ? 'Ocultar opciones avanzadas' : 'Mostrar opciones avanzadas (WhatsApp, JSON módulos…)'}
              </button>
            </div>
            )}
            {!gymId && showAdvanced && (
            <div className="border-t border-stone-200 pt-4 mt-2 space-y-4">
              <p className="text-sm font-semibold text-stone-800">Soporte WhatsApp (wa.me)</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <PhoneField
                  label="WhatsApp soporte"
                  value={formData.support_whatsapp}
                  onChange={(v) => setFormData((prev) => ({ ...prev, support_whatsapp: v }))}
                  inputClassName="input"
                  placeholder="319 263 5819"
                />
                <input className="input" name="support_name" placeholder="Nombre soporte"
                  value={formData.support_name} onChange={handleInputChange} />
                <textarea className="input md:col-span-2" name="support_message" rows={2}
                  placeholder="Mensaje precargado en WhatsApp"
                  value={formData.support_message} onChange={handleInputChange} />
                <label className="flex items-center gap-2 text-sm md:col-span-2">
                  <input type="checkbox" name="support_activo" checked={!!formData.support_activo}
                    onChange={(e) => setFormData((p) => ({ ...p, support_activo: e.target.checked }))} />
                  Soporte activo
                </label>
              </div>
              {fixedKind !== 'training' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <input
                    id="is_couple"
                    name="is_couple"
                    type="checkbox"
                    checked={!!formData.is_couple}
                    onChange={(e) => setFormData((prev) => ({ ...prev, is_couple: e.target.checked }))}
                  />
                  <label htmlFor="is_couple" className="label" style={{ marginBottom: 0 }}>Plan de pareja</label>
                </div>
                <div>
                  <label className="label">Cupos incluidos</label>
                  <input
                    name="included_seats"
                    type="number"
                    value={formData.included_seats}
                    onChange={handleInputChange}
                    className="input"
                    min="1"
                    max="2"
                  />
                </div>
              </div>
              )}
              <div>
                <label className="label">Acceso a módulos (JSON técnico)</label>
                <textarea
                  name="module_access"
                  value={formData.module_access}
                  onChange={handleInputChange}
                  rows="5"
                  className="input font-mono text-xs"
                />
              </div>
            </div>
            )}
            {fixedKind !== 'training' && formData.kind !== 'training' && !showAdvanced && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <input
                  id="is_couple_simple"
                  name="is_couple"
                  type="checkbox"
                  checked={!!formData.is_couple}
                  onChange={(e) => setFormData((prev) => ({ ...prev, is_couple: e.target.checked }))}
                />
                <label htmlFor="is_couple_simple" className="label" style={{ marginBottom: 0 }}>Plan de pareja</label>
              </div>
              <div>
                <label className="label">Cupos incluidos</label>
                <input
                  name="included_seats"
                  type="number"
                  value={formData.included_seats}
                  onChange={handleInputChange}
                  className="input"
                  min="1"
                  max="2"
                />
              </div>
            </div>
            )}
            <div>
              <label className="label">{t('plans.features', 'Características (separadas por coma)')}</label>
              <input 
                name="features" 
                value={formData.features} 
                onChange={handleInputChange} 
                placeholder={t('plans.features_ph', 'Ej: Acceso 24/7, Entrenador Personal')}
                className="input"
              />
            </div>
            <div className="flex justify-end pt-2">
              <button type="submit" className="btn-primary">
                {t('plans.save', 'Guardar Plan')}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden ui-light-panel admin-plans-table">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-stone-50">
                <tr>
                  {showProgramColumn && (
                    <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">Programa</th>
                  )}
                  {fixedKind === 'training' && (
                    <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">Aplica a</th>
                  )}
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">{t('common.name', 'Plan')}</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">Usuarios</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider notranslate" translate="no">Precio COP</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider notranslate" translate="no">Precio USD</th>
                  {showTrainingMonths && (
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">Meses</th>
                  )}
                  {showD28dCycles && (
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">Ciclos</th>
                  )}
                  {(fixedKind === 'd28d' || !fixedKind) && (
                    <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">
                      {t('plans.for_sedes', 'Sedes')}
                    </th>
                  )}
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">Estado</th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-semibold text-stone-600 uppercase tracking-wider">{t('common.actions', 'Acciones')}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {loading ? (
                  <tr><td colSpan={tableColSpan} className="px-6 py-4 text-center text-sm text-slate-600">{t('plans.loading', 'Cargando planes...')}</td></tr>
                ) : plans.length > 0 ? (
                  plans.map((plan) => (
                    <tr key={plan.nombre} className="hover:bg-stone-50 transition-colors">
                      {showProgramColumn && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-stone-900 capitalize">
                          {programs.find((p) => String(p.id) === String(plan.program_id))?.name || plan.program_id}
                        </td>
                      )}
                      {fixedKind === 'training' && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-stone-700">
                          {TRAINING_AUDIENCE_LABELS[plan.training_audience || 'client'] || plan.training_audience || '—'}
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-stone-900">{planLabel(plan)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-stone-700">{plan.included_seats || plan.max_usuarios || 1}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-stone-700">${Number(plan.precio_mensual || 0).toLocaleString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-stone-700">{plan.precio_mensual_usd || '—'}</td>
                      {showTrainingMonths && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-stone-700">{plan.cycles_count ?? 1}</td>
                      )}
                      {showD28dCycles && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-stone-700">{plan.cycles_count ?? '—'}</td>
                      )}
                      {(fixedKind === 'd28d' || !fixedKind) && (
                        <td className="px-6 py-4 whitespace-nowrap text-xs">
                          {plan.for_sedes === true ? (
                            <span className="px-2 py-0.5 rounded-full bg-sky-100 text-sky-800">Sedes</span>
                          ) : (
                            <span className="text-stone-400">—</span>
                          )}
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap text-xs">
                        <span className={`px-2 py-0.5 rounded-full ${isPlanActive(plan) ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-200 text-stone-600'}`}>
                          {isPlanActive(plan) ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2 flex-wrap items-center">
                          <button
                            type="button"
                            className="text-lime-800 hover:text-black bg-stone-100 hover:bg-lime-400 p-2 rounded-lg"
                            onClick={() => handleEdit(plan)}
                            title="Editar precio y características"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            className={`text-xs px-3 py-1.5 rounded-lg border font-medium ${
                              isPlanActive(plan)
                                ? 'border-stone-300 text-stone-700 hover:bg-stone-100'
                                : 'border-emerald-500 text-emerald-800 bg-emerald-50 hover:bg-emerald-100'
                            }`}
                            onClick={() => togglePlanActive(plan)}
                          >
                            {isPlanActive(plan) ? 'Desactivar' : 'Activar'}
                          </button>
                          <button
                            type="button"
                            className="text-white bg-red-600 hover:bg-red-700 p-2 rounded-lg"
                            onClick={() => handleDelete(plan.nombre)}
                            title="Eliminar plan"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={tableColSpan} className="px-6 py-4 text-center text-sm text-slate-600">{t('plans.none', 'No hay planes disponibles. Crea uno nuevo.')}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
